<script setup>
import { computed, markRaw, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';

import StartNode from './nodes/StartNode.vue';
import EndNode from './nodes/EndNode.vue';
import DecisionNode from './nodes/DecisionNode.vue';
import RouteNode from './nodes/RouteNode.vue';
import ActionNode from './nodes/ActionNode.vue';
import AutomationNode from './nodes/AutomationNode.vue';
import ReferenceNode from './nodes/ReferenceNode.vue';
import {
  NODE_META,
  NODE_TYPES,
  createEdge,
  createNode,
  findStartNode,
  removeEdgesForSourceHandle,
} from '../helpers/sopFlow';

// Vue Flow ships its own stylesheets; vendored exception to the Tailwind-only rule.
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

const props = defineProps({
  readonly: { type: Boolean, default: false },
  initialViewport: { type: Object, default: null },
});

const emit = defineEmits(['nodeSelected', 'deleteNode', 'duplicateNode']);

const nodes = defineModel('nodes', { type: Array, required: true });
const edges = defineModel('edges', { type: Array, required: true });

const { t } = useI18n();

const nodeTypes = {
  [NODE_TYPES.START]: markRaw(StartNode),
  [NODE_TYPES.END]: markRaw(EndNode),
  [NODE_TYPES.DECISION]: markRaw(DecisionNode),
  [NODE_TYPES.ROUTE]: markRaw(RouteNode),
  [NODE_TYPES.ACTION]: markRaw(ActionNode),
  [NODE_TYPES.AUTOMATION]: markRaw(AutomationNode),
  [NODE_TYPES.REFERENCE]: markRaw(ReferenceNode),
};

const ADD_NODE_TYPES = [
  NODE_TYPES.START,
  NODE_TYPES.DECISION,
  NODE_TYPES.ROUTE,
  NODE_TYPES.ACTION,
  NODE_TYPES.AUTOMATION,
  NODE_TYPES.REFERENCE,
  NODE_TYPES.END,
];

const { onConnect, screenToFlowCoordinate, toObject } = useVueFlow();

onConnect(params => {
  if (props.readonly) return;
  if (params.source === params.target) return;
  edges.value = [
    ...removeEdgesForSourceHandle(
      edges.value,
      params.source,
      params.sourceHandle
    ),
    createEdge(params),
  ];
});

const onNodeClick = ({ node }) => {
  if (props.readonly) return;
  emit('nodeSelected', node);
};

// Right-click menu: 'add' (blank canvas) or 'node' (existing node).
const contextMenu = reactive({
  mode: null,
  x: 0,
  y: 0,
  flowPosition: null,
  nodeId: null,
});

const onPaneClick = () => {
  emit('nodeSelected', null);
  contextMenu.mode = null;
};

const MENU_WIDTH = 260;
const MENU_HEIGHT_ESTIMATE = 320;

const clampedPosition = (clientX, clientY) => ({
  x: Math.min(clientX, window.innerWidth - MENU_WIDTH - 8),
  y: Math.min(clientY, window.innerHeight - MENU_HEIGHT_ESTIMATE - 8),
});

const closeMenu = () => {
  contextMenu.mode = null;
};

const onPaneContextMenu = event => {
  event.preventDefault();
  if (props.readonly) return;
  const { x, y } = clampedPosition(event.clientX, event.clientY);
  contextMenu.mode = 'add';
  contextMenu.x = x;
  contextMenu.y = y;
  contextMenu.flowPosition = screenToFlowCoordinate({
    x: event.clientX,
    y: event.clientY,
  });
};

const onNodeContextMenu = ({ event, node }) => {
  event.preventDefault();
  if (props.readonly) return;
  const { x, y } = clampedPosition(event.clientX, event.clientY);
  contextMenu.mode = 'node';
  contextMenu.x = x;
  contextMenu.y = y;
  contextMenu.nodeId = node.id;
};

const addableNodeTypes = computed(() =>
  ADD_NODE_TYPES.filter(
    type => !(type === NODE_TYPES.START && findStartNode(nodes.value))
  )
);

const addNode = type => {
  nodes.value.push(createNode(type, contextMenu.flowPosition));
  closeMenu();
};

const contextNode = computed(() =>
  nodes.value.find(node => node.id === contextMenu.nodeId)
);

const duplicateNode = () => {
  emit('duplicateNode', contextMenu.nodeId);
  closeMenu();
};

const deleteNode = () => {
  emit('deleteNode', contextMenu.nodeId);
  closeMenu();
};

defineExpose({ toObject });
</script>

<template>
  <div
    class="relative h-full w-full overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
  >
    <VueFlow
      v-model:nodes="nodes"
      v-model:edges="edges"
      :node-types="nodeTypes"
      :nodes-draggable="!readonly"
      :nodes-connectable="!readonly"
      :elements-selectable="!readonly"
      :default-viewport="initialViewport || undefined"
      :fit-view-on-init="!initialViewport"
      :delete-key-code="readonly ? null : 'Backspace'"
      @node-click="onNodeClick"
      @pane-click="onPaneClick"
      @pane-context-menu="onPaneContextMenu"
      @node-context-menu="onNodeContextMenu"
    >
      <Background />
      <Controls :show-interactive="false" />
    </VueFlow>

    <Teleport to="body">
      <div
        v-if="contextMenu.mode"
        class="fixed inset-0 z-40"
        @click="closeMenu"
        @contextmenu.prevent="closeMenu"
      >
        <div
          class="absolute z-50 w-64 rounded-lg border border-n-weak bg-n-solid-2 p-1 shadow-lg"
          :style="{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }"
          @click.stop
        >
          <template v-if="contextMenu.mode === 'add'">
            <p
              class="mb-0 px-2 py-1.5 text-xs uppercase tracking-wide text-n-slate-10"
            >
              {{ t('SOPS.BUILDER.CONTEXT_MENU.ADD_NODE_TITLE') }}
            </p>
            <button
              v-for="type in addableNodeTypes"
              :key="type"
              type="button"
              class="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-n-alpha-black2"
              @click="addNode(type)"
            >
              <span
                class="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md"
                :class="NODE_META[type].accentClass"
              >
                <span :class="NODE_META[type].icon" class="size-3.5" />
              </span>
              <span class="min-w-0">
                <span class="block text-sm text-n-slate-12">
                  {{ t(`SOPS.NODES.${type.toUpperCase()}`) }}
                </span>
                <span class="block text-xs text-n-slate-10">
                  {{ t(`SOPS.NODES_HELP.${type.toUpperCase()}`) }}
                </span>
              </span>
            </button>
          </template>

          <template v-else-if="contextMenu.mode === 'node' && contextNode">
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-n-slate-12 enabled:hover:bg-n-alpha-black2 disabled:cursor-not-allowed disabled:text-n-slate-9"
              :disabled="contextNode.type === NODE_TYPES.START"
              @click="duplicateNode"
            >
              <span class="i-lucide-copy size-4" />
              {{ t('SOPS.BUILDER.CONTEXT_MENU.DUPLICATE') }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-n-ruby-11 hover:bg-n-alpha-black2"
              @click="deleteNode"
            >
              <span class="i-lucide-trash-2 size-4" />
              {{ t('SOPS.BUILDER.CONTEXT_MENU.DELETE') }}
            </button>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style>
/* Vendored override: Vue Flow's default handle (6px) is hard to grab/click. */
.vue-flow__handle {
  width: 10px;
  height: 10px;
  border-width: 2px;
}

/* Vendored override: default Controls buttons sit flush against each other. */
.vue-flow__controls-button:not(:last-child) {
  margin-bottom: 6px;
}
</style>
